import {
    Component,
    inject,
    Input,
    Output,
    EventEmitter,
    OnInit,
    ViewEncapsulation,
} from "@angular/core";
import { TranslatePipe, injectTranslation, provideTranslationScope } from "@app/shared/i18n/translation-scope";
import { scope } from "./i18n/scope";
import { MatIconModule } from "@angular/material/icon";
import { MatDialogModule } from "@angular/material/dialog";
import { AssetHyperlinkModel } from "@models/assetmanagement/assethyperlinkmodel";
import { CerrixButtonComponent } from "@app/shared/atoms/cerrix-button/cerrix-button.component";
import { StandingdataDataService } from "@app/standingdata/shared/standingdata.service";
import { CerrixDialogComponent } from "../cerrix-dialog/cerrix-dialog.component";
import { CerrixInputGroupComponent } from "@app/shared/molecules/cerrix-input-group/cerrix-input-group.component";
import { CerrixSelectGroupComponent } from "@app/shared/molecules/cerrix-select-group/cerrix-select-group.component";
import { TargetModule } from "@enums/document/TargetModule";
import { CerrixTextareaGroupComponent } from "@app/shared/molecules/cerrix-textarea-group/cerrix-textarea-group.component";
import { CerrixHyperlinkUrlInputGroupComponent } from "@app/shared/molecules/cerrix-hyperlink-url-input-group/cerrix-hyperlink-url-input-group.component";
import { CerrixInputValidationRegistryService } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-registry.service";
import { ToastrService } from "ngx-toastr";
import { OpenAddHyperlinkDialogReason } from "./models/open-dialog-reason-model";
import { CerrixInputValidationRequiredDirective } from "@app/shared/directives/cerrix-input-fields/cerrix-input-validation-required.directive";

@Component({
    selector: "app-cerrix-add-hyperlink-dialog",
    templateUrl: "./cerrix-add-hyperlink-dialog.component.html",
    styleUrls: ["./cerrix-add-hyperlink-dialog.component.scss"],
    imports: [
        MatIconModule,
        MatDialogModule,
        CerrixButtonComponent,
        CerrixInputGroupComponent,
        CerrixSelectGroupComponent,
        CerrixDialogComponent,
        TranslatePipe,
        CerrixTextareaGroupComponent,
        CerrixHyperlinkUrlInputGroupComponent,
        CerrixInputValidationRequiredDirective,
    ],
    viewProviders: [
        provideTranslationScope(scope),
    ],
    encapsulation: ViewEncapsulation.None,
})
export class CerrixAddHyperlinkDialogComponent implements OnInit {
    @Input() public name: string;
    @Input() public hyperlink: AssetHyperlinkModel | null;
    @Input() public openReason: OpenAddHyperlinkDialogReason;

    @Output() public cancelDialog = new EventEmitter<AssetHyperlinkModel>();
    @Output() public confirmDialog = new EventEmitter<AssetHyperlinkModel>();

    protected editHyperlink: AssetHyperlinkModel = new AssetHyperlinkModel();
    protected dataUiid: string = "cerrix-add-hyperlink-dialog";
    protected readonly OpenAddHyperlinkDialogReason = OpenAddHyperlinkDialogReason;

    protected hyperlinkTypeOptions: { label: string; value: string }[] = [];
    protected selectedHyperlinkType: string | null;

    private _sdDataService: StandingdataDataService = inject(StandingdataDataService);
    private _translation = injectTranslation(scope);
    private _validationRegistry = inject(CerrixInputValidationRegistryService);
    private _toastService = inject(ToastrService);

    async ngOnInit(): Promise<void> {
        await this._translation.ready();

        this._sdDataService
            .getHyperlinkTypes(TargetModule.AssetManagement)
            .subscribe((hyperlinkTypes) => {
                this.hyperlinkTypeOptions = hyperlinkTypes.map((t) => ({
                    label: t.Name,
                    value: t.HyperlinkTypeGuid,
                }));
            });
    }

    public setDialogData(
        hyperlink: AssetHyperlinkModel | null,
        reason: OpenAddHyperlinkDialogReason,
    ): void {
        this._validationRegistry.resetErrors();
        this.hyperlink = hyperlink;
        this.openReason = reason;
        this.syncEditHyperlink();
    }

    private syncEditHyperlink(): void {
        this.editHyperlink = this.hyperlink
            ? Object.assign(new AssetHyperlinkModel(), this.hyperlink)
            : new AssetHyperlinkModel();
    }

    onHyperlinkNameChange(value: string): void {
        this.editHyperlink.name = value;
    }

    onHyperlinkTypeChange(value: string): void {
        this.editHyperlink.hyperlinkType = value;
    }

    onHyperlinkUrlChange(value: string): void {
        this.editHyperlink.hyperlinkUrl = value;
    }

    onHyperlinkDescriptionChange(value: string): void {
        this.editHyperlink.description = value;
    }

    saveHyperlink(): void {
        if (!this.validateFields()) return;
        this.confirmDialog.emit(this.editHyperlink);
    }

    private validateFields(): boolean {
        this._validationRegistry.runValidators();
        if (this._validationRegistry.hasAnyErrors()) {
            this._toastService.warning(
                this._validationRegistry
                    .allMessages()
                    .map((e) => "- " + e)
                    .join("\n"),
                this._translation.t("saveFailed"),
            );
            return false;
        }
        return true;
    }
}
